/**
 * Runs synthesis, preferring a Web Worker but transparently falling back to the
 * main thread (chunked so the UI still paints) when workers are unavailable —
 * e.g. inside a strict single-file sandbox.
 */

import type { WorkerRequest, WorkerResponse } from "./protocol.js";

export interface SynthHandle {
  cancel(): void;
}

export interface RunnerCallbacks {
  onMessage(msg: WorkerResponse): void;
}

function optionsFromRequest(req: Extract<WorkerRequest, { type: "synthesize" }>) {
  return {
    topology: req.topology,
    seed: req.seed,
    restarts: req.restarts,
    itersPerRestart: req.itersPerRestart,
    polishIters: req.polishIters,
    samples: req.samples,
    refineSamples: req.refineSamples,
    allowScale: req.allowScale,
  };
}

/** Replaced at build time; `true` only in the single-file (`--mode embed`) build. */
declare const __INLINE_ONLY__: boolean;
const INLINE_ONLY = typeof __INLINE_ONLY__ !== "undefined" && __INLINE_ONLY__;

export function startSynthesis(
  req: Extract<WorkerRequest, { type: "synthesize" }>,
  cb: RunnerCallbacks,
): SynthHandle {
  if (INLINE_ONLY) {
    const handle = { cancelled: false, cancel() { this.cancelled = true; } };
    runInline(req, cb, handle);
    return handle;
  }
  // --- try a real worker ---
  try {
    const worker = new Worker(new URL("./worker.ts", import.meta.url), {
      type: "module",
    });
    worker.onmessage = (e: MessageEvent<WorkerResponse>) => {
      cb.onMessage(e.data);
      if (e.data.type === "done" || e.data.type === "error") worker.terminate();
    };
    worker.onerror = () => {
      worker.terminate();
      runInline(req, cb, handle);
    };
    worker.postMessage(req);
    const handle: SynthHandle & { cancelled: boolean } = {
      cancelled: false,
      cancel() {
        this.cancelled = true;
        worker.postMessage({ type: "cancel" } satisfies WorkerRequest);
      },
    };
    return handle;
  } catch {
    // --- main-thread fallback ---
    const handle: SynthHandle & { cancelled: boolean } = {
      cancelled: false,
      cancel() {
        this.cancelled = true;
      },
    };
    runInline(req, cb, handle);
    return handle;
  }
}

function runInline(
  req: Extract<WorkerRequest, { type: "synthesize" }>,
  cb: RunnerCallbacks,
  handle: { cancelled: boolean },
): void {
  // Defer so the "synthesizing…" UI paints before we hog the thread. The engine
  // is dynamically imported so it stays out of the main chunk when a worker is
  // available (the common case).
  setTimeout(async () => {
    try {
      const { synthesize } = await import("../synthesis/optimize.js");
      const result = synthesize(req.target, {
        ...optionsFromRequest(req),
        shouldStop: () => handle.cancelled,
        onProgress: (p) => {
          cb.onMessage({
            type: "progress",
            phase: p.phase,
            restart: p.restart,
            restarts: p.restarts,
            iter: p.iter,
            iters: p.iters,
            loss: p.loss,
            bestLoss: p.bestLoss,
            parts: p.parts,
            spec: p.spec,
            curve: p.curve,
            convergedFraction: p.convergedFraction,
          });
        },
      });
      cb.onMessage({ type: "done", result });
    } catch (err) {
      cb.onMessage({
        type: "error",
        message: err instanceof Error ? err.message : String(err),
      });
    }
  }, 60);
}
